import React, { useEffect, useState } from 'react';
import api from '../../services/api';

function MoneyCard({ title, stats }) {
  const s = stats || {};
  const total = Number(s.totalRevenue ?? 0);
  return (
    <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-3xl font-bold tabular-nums text-emerald-700 mt-2">
        ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      <p className="text-xs text-slate-500 mt-2">{s.paymentCount ?? 0} paid subscriptions</p>
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/performance/admin/revenue')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-center text-slate-400 py-16">Loading revenue…</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Subscription revenue</h1>
        <p className="text-sm text-slate-500">Paid user subscriptions (Stripe / recorded payments)</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <MoneyCard title="1 month" stats={data?.month} />
        <MoneyCard title="Quarter" stats={data?.quarter} />
        <MoneyCard title="6 months" stats={data?.sixMonths} />
        <MoneyCard title="1 year" stats={data?.year} />
        <MoneyCard title="All time" stats={data?.allTime} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">Revenue by month</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-right px-3 py-2">Payments</th>
              <th className="text-right px-3 py-2">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(data?.byMonth || []).map(m => (
              <tr key={m.month} className="border-t border-slate-50">
                <td className="px-3 py-2 font-medium">{m.month}</td>
                <td className="px-3 py-2 text-right">{m.paymentCount}</td>
                <td className="px-3 py-2 text-right font-bold text-emerald-700">
                  ${Number(m.totalRevenue ?? 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b text-sm font-semibold">Recent payments</div>
        {(data?.recent || []).length === 0 ? (
          <p className="p-4 text-sm text-slate-400">No paid subscriptions recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-right px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent || []).map(r => (
                  <tr key={r.id} className="border-t border-slate-50">
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {r.paidAt ? new Date(r.paidAt).toLocaleString() : (r.createdAt ? new Date(r.createdAt).toLocaleString() : '—')}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium">{r.userName || r.userEmail || `User #${r.userId}`}</p>
                      <p className="text-[11px] text-slate-400">{r.userEmail}</p>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.description || '—'}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-700">
                      ${Number(r.amount ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
